/**
 * 오늘출발(배포 2b) 셀러 화면 문구 — **한 곳에 모아 둔다**.
 *
 * ## 이 파일에 있는 것 / 없는 것
 * - **있다**: 정본(플랜·계약·위키)에 없어 이번에 새로 쓴 문구(키 108 · 고유 93). PM 승인 대기 중이며,
 *   승인 결과가 달라지면 **이 파일만 고치면 된다**(보고서 §9 와 1:1).
 *   보고서 §9 는 이를 **A(판단 필요, 고유 43)** 와 **B(기계적, 50)** 로 가르고, 실제 결정이 갈리는
 *   5개(§9-1)를 앞에 둔다 — 상품 일괄 변경 확인 · 등록 직후 부분 실패 · 배송정책 부분 저장 ·
 *   실패 사유 `TD_NOT_OWNER` · 앱 노출 상태 '일반배송' 설명.
 * - **문체**(PM 2026-09-28): 해요체 금지 — 서술은 합니다체, 요청만 "~해 주세요". 하니스가 이 파일 전체를 검사한다.
 * - **없다(다른 곳이 정본)**:
 *   - 동의 문안 8항목 → 서버 단일 원천(`policy_agreement`, adminapi `policy_agreement.json`)
 *   - 중지·참여 게이트·컷오프 30일 1회 안내 → adminapi `service.py` 와 **같은 문자열**(아래 SERVER_MIRROR)
 *   - '오늘출발 미출고'(대시보드) → 플랜 §10-3 · '오늘출발'(주문 배송구분) → 계약 §2 값 라벨
 *   - '전체/일부/미적용' → 플랜 §3-1 2b-A4 표현 (`constants/todayDelivery.APPLY_TYPE_ITEMS`)
 *   - 기존 화면에서 그대로 가져온 목록 컬럼명(상품번호·상품코드·대표이미지·상품명·판매가·
 *     할인판매가·진열상태·판매상태·등록일·배송구분) → 상품 리스트 선례
 */

/** adminapi `today_delivery/service.py` 의 메시지와 **같은 문자열**이어야 한다(서버가 같은 상황에 같은 말을 한다). */
export const SERVER_MIRROR = {
  suspended: '운영자가 오늘출발을 중지한 상태입니다. 고객센터로 문의해 주세요.',
  participationLocked: '오늘출발 참여가 제한된 스토어입니다. 하이버 판매자 지원센터에 문의해 주세요.',
  // 컷오프 변경 제한 — 마지막 변경 기준 30일에 1회(롤링, PM 2026-09-28 — 달력월 1회 대체). 오류 코드는 `TD_CUTOFF_MONTHLY_LIMIT` 그대로.
  // 다시 바꿀 수 있는 날(`error_data.next_available`)이 오면 화면이 `POLICY_SECTION.cutoffLimitAvailableFrom` 을 뒤에 붙인다.
  cutoffChangeLimit: '주문 마감시각은 30일에 한 번만 변경할 수 있습니다.',
  agreementRequired: '오늘출발 정책에 동의해야 사용할 수 있습니다.',
}

/** 2b-S1 배송정책 관리 > 오늘출발 섹션 */
export const POLICY_SECTION = {
  heading: '오늘출발',
  guideLink: '오늘출발 가이드',
  useLabel: '오늘출발 사용 여부',
  cutoffLabel: '주문 마감시각 *',
  cutoffOptionSuffix: '마감',
  // 마감시각 아래 힌트 — 30일에 1회(롤링, PM 2026-09-28). `{date}` 는 `cutoffDate`·`cutoffDateWithYear` 로 만든 날짜
  cutoffAvailableHint: '한 번 변경하면 30일 동안 다시 변경할 수 없습니다.',
  cutoffLockedHint: '{date}부터 다시 변경할 수 있습니다.',
  /** 거절 안내(`SERVER_MIRROR.cutoffChangeLimit`) 뒤에 붙는 날짜 문장 */
  cutoffLimitAvailableFrom: '{date}부터 다시 변경할 수 있습니다.',
  /** 다시 바꿀 수 있는 날 — 올해면 월·일만, 해가 바뀌면 연도까지 */
  cutoffDate: '{M}월 {D}일',
  cutoffDateWithYear: '{Y}년 {M}월 {D}일',
  weekdayNotice: '주말·공휴일 주문은 다음 영업일 출발',
  // 출고지 4필드는 화면에서 내렸다(PM 결정 2026-09-21) — 소비처가 없어 셀러 입력만 늘었다.
  // 문구는 남겨 둔다. 소비처가 생기면 `TodayDeliverySetting.vue` 의 주석 블록만 되살리면 된다.
  releaseZipLabel: '출고지 우편번호',
  releaseZipPlaceholder: '우편번호 검색',
  releaseAddressLabel: '출고지 주소',
  releaseAddressPlaceholder: '우편번호를 검색해 주세요',
  releaseAddressDetailLabel: '출고지 상세주소',
  releaseTelLabel: '출고지 전화번호',
  inputPlaceholder: '입력해 주세요.',
  // 정책 동의 본문(`TodayDeliveryAgreementPanel`) — 오늘출발관 신청 페이지의 카드 형식(PM 2026-09-30). 신청 페이지는 정책 전문이 아니라 링크는 뺐다(PM).
  // 창 제목·리드·버튼은 오늘출발 시작 설정 팝업(`START_SETUP`)의 것이다 — 배송정책 관리의 옛 동의 모달은 없앴다(PM 2026-10-13)
  agreementAll: '전체 동의',
  agreementAllDesc: '아래 운영 기준과 페널티(정산 차감) 내용을 모두 확인했으며 동의합니다.',
  agreementSectionOperation: '운영 기준',
  agreementSectionPenalty: '페널티(정산 차감)',
  // 서버 그룹 `important: true` 카드 제목 옆 칩 — 신청 페이지 칩과 같은 글자(문안 아님, PM 2026-10-12)
  agreementImportant: '중요',
  agreementCheck: '위 내용을 확인했으며 동의합니다.',
  loadFailed: '오늘출발 설정을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  // 서버에서 동의 문안을 못 받았을 때 — 빈 본문에 동의시키지 않는다(fail-closed)
  agreementUnavailable: '오늘출발 정책 문안을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  saveFailed: '오늘출발 설정을 저장하지 못했습니다.',
  /** 저장 실패 안내 뒤에 붙는다 — 무엇이 저장됐고 무엇을 다시 눌러야 하는지 */
  // PM 결정(2026-09-15 ③) — 저장 버튼은 하나로 유지. 이 문구가 부분 저장 상태를 혼자 감당하므로
  // "입력값이 그대로 남아 있다" 는 사실을 한 조각 넣어 처음부터 다시 입력하지 않게 한다.
  retrySuffix: '다른 배송정책은 저장됐고 입력한 내용은 그대로 있습니다 — 다시 [배송정책 저장]을 눌러 주세요.',
  /** 부모(배송정책 화면)가 저장을 멈출 때 띄우는 스낵바 */
  checkBeforeSave: '오늘출발 설정을 확인해 주세요.',
  /** 저장 실패 항목 집계 라벨 */
  problemLabel: '오늘출발 설정',
  soldoutLabel: '재고 소진 시 판매방식',
  soldoutHint: '앞으로 만드는 옵션에 적용됩니다. 이미 있는 옵션은 그대로 유지됩니다.',
}

/**
 * 오늘출발 시작 설정 팝업(PM 2026-10-13) — 정책을 쓰지 않는 셀러가 지정 진입점(옵션 관리 [추가]·[엑셀 일괄], 상품 리스트
 * [배송변경] > [오늘출발 지정], 옵션 편집기·옵션 없는 상품의 배송타입 칸, 배송정책 관리 스위치)에서 정책을 먼저 켠다.
 * 1단계 기본 설정(사용 여부·주문 마감시각·재고 소진 시 판매방식) → 2단계 정책 동의(배송정책 관리 동의 카드와 같은 본문).
 * 칸 이름·도움 글자는 배송정책 관리(`POLICY_SECTION`)와 같은 문구를 쓴다.
 */
export const START_SETUP = {
  title: '오늘출발 시작 설정',
  lead: '오늘출발을 시작하려면 기본 설정을 저장하고 운영 기준·페널티에 동의해 주세요. 동의가 끝나면 바로 상품을 지정할 수 있습니다.',
  stepSettings: '기본 설정',
  stepAgreement: '정책 동의',
  // '오늘출발 사용 여부' 줄 — 값은 늘 '사용'(읽기 전용)이고 뒤에 저장 안내가 붙는다
  useValue: '사용',
  useHint: '저장하면 사용으로 바뀝니다.',
  exampleNote: '다음 단계의 예시는 선택한 주문 마감시각 기준으로 보입니다.',
  cancel: '취소',
  next: '다음',
  back: '이전',
  start: '오늘출발 시작',
  // 저장 실패 — 서버가 문구를 주면(참여 제한·중지·마감시각 30일 1회 등) 그 문장을 쓴다
  saveFailed: '오늘출발 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  // 시작 성공 스낵바 — 진입점이 고른다(옵션 관리·상품 리스트 / 상품 등록·수정 / 배송정책 관리)
  startedAssignProducts: '오늘출발을 사용으로 설정했습니다. 이어서 상품을 지정해 주세요.',
  startedAssignOptions: '오늘출발을 사용으로 설정했습니다. 이어서 옵션을 지정해 주세요.',
  started: '오늘출발을 사용으로 설정했습니다.',
}

/** 2b-S2 상품 리스트 */
export const PRODUCT_LIST = {
  // 필터 값이 '일반배송 / 오늘출발 / 오늘출발(일부)' 로 바뀌어(2026-09-15 PM) '오늘출발 적용' 은
  // 더 이상 필터 전체를 가리키지 못한다. SKU 배송타입과 같은 것을 거르므로 같은 말을 쓴다.
  filterPlaceholder: '오늘출발 설정',
  // 필터 항목(일반배송 / 오늘출발 / 오늘출발(일부))과 **같은 단어**로 값을 보여 준다
  deliverColumn: '오늘출발 설정',
  menuButton: '배송변경',
  settingsShortcut: '오늘출발 설정',
  setToday: '오늘출발 지정',
  unsetToday: '오늘출발 해제',
  dialogTitle: '배송변경',
  // [배송변경] 확인 문장 — 바꿀 값마다 조사가 맞는 완성 문장(오늘출발**로** / 일반배송**으로**). `{n}` = 상품 수, `{label}` 자리는 화면이 굵게 그린다
  changeConfirmToday: '선택한 상품 {n}개를 {label}로 변경합니다.',
  changeConfirmNormal: '선택한 상품 {n}개를 {label}으로 변경합니다.',
  // PM/모더레이터 결정(2026-09-15 ①) — '함께 변경' 은 덮어쓴다는 뜻이 약해 되돌릴 길을 같이 준다
  optionNotice: '이 상품의 옵션이 모두 오늘출발로 바뀝니다. 옵션마다 다르게 두려면 오늘출발 옵션 관리에서 지정해 주세요.',
  optionNoticeUnset: '이 상품의 옵션이 모두 일반배송으로 바뀝니다. 옵션마다 다르게 두려면 오늘출발 옵션 관리에서 지정해 주세요.',
  // 배송변경(오늘출발 지정) 대상 — adminapi `writer._reject_reason` 과 같은 규칙: 내 상품(삭제 아님)이고 국내배송이어야 한다.
  // 판매상태(판매중지 N/M)는 보지 않는다 — 판매중지 상품도 지정할 수 있다(PM 2026-09-28 유지 결정). `FAILURE_REASON.TD_NOT_OWNER` 참고
  domesticNotice: '내 국내배송 상품만 지정할 수 있습니다.',
  failureColumnNo: '상품번호',
  failureColumnReason: '변경하지 못한 사유',
  /** 앞에 건수가 붙는다 — "12개 상품이 변경되었습니다." */
  changeSuccessSuffix: '개 상품이 변경되었습니다.',
  changeFailed: '배송변경에 실패하였습니다. 다시 시도해 주세요.',
  // 상품 목록(adminapi `GET /v1/sellers/{md_no}/products`, 260928 설계 9) 조회 실패 — 아래 `SELLER_PRODUCTS_ERROR` 표에 없는 오류 전부
  listFailed: '상품 목록을 불러오지 못했습니다. 다시 시도해 주세요.',
  // 엑셀(adminapi, 배송구분 열 포함) 저장 파일명
  excelFileName: '상품_목록.xlsx',
}

/**
 * 상품 목록(adminapi `GET /v1/sellers/{md_no}/products`) 오류 → 화면 문구 (`SellerProductsApi.sellerProductsErrorText`).
 * 스낵바는 `v-html` 로 그리므로 **서버 문구(`meta.message`)는 어떤 경우에도 쓰지 않는다** — 오류 코드·상태별로 이 표의 문구만 보여 주고,
 * 표에 없으면 `PRODUCT_LIST.listFailed`. 코드는 adminapi `domain/seller_products/constants.py` 의 `SP_` 접두.
 */
export const SELLER_PRODUCTS_ERROR = {
  // 400 `SP_TOO_MANY_PRODUCTS` — `productNos`·`productSkus` 각 3,000개(중복 제외) 초과
  tooManyProducts: '상품번호는 한 번에 3,000개까지 조회할 수 있습니다.',
  // 400 `SP_INVALID_PRODUCT_NOS` — 상품번호가 숫자가 아니다
  invalidProductNos: '상품번호 형식이 올바르지 않습니다.',
  // 400 `SP_INVALID_DELIVER_CODE` — `deliverCode` 가 01·02·02p 가 아니다(주소에 남은 옛 값 등)
  invalidDeliverCode: '오늘출발 설정 필터 값이 올바르지 않습니다.',
  // 403 — 다른 셀러 번호(`resolve_md_no`)·권한 없음. 인증 만료(401)와 달리 로그아웃하지 않는다
  forbidden: '이 상품 목록을 조회할 권한이 없습니다.',
  // 401 — 인증 만료, 로그아웃과 함께 띄운다
  authExpired: '인증이 만료되었습니다. 로그인해 주세요.',
}

/** 2b-S3 오늘출발 옵션 관리 */
export const OPTION_PAGE = {
  unavailable: '오늘출발 옵션 관리를 사용할 수 없습니다.',
  // 목록 조회 한 번이 실패했다(400·500·네트워크) — 화면은 그대로 두고 목록 자리에만 알린다(다시 [검색] 하면 된다, 스테이징 R3 2026-10-02)
  listFailed: '오늘출발 옵션 목록을 불러오지 못했습니다. 다시 시도해 주세요.',
  // 요약 — 목록에 오늘출발 옵션이 있는 **상품 수**를 옵션 적용 범위로 나눈 값이다(PM 2026-09-30 "무엇의 전체/일부인지 알 수 없다").
  // 상단 요약 띠의 첫 칸: 제목 + 값 2개. `{n}` = 상품 수 자리 — 화면이 이 자리에서 문구를 갈라 **숫자만 크게** 그린다(천 단위 구분, 0 도 `0`)
  summaryTitle: '오늘출발 상품',
  summaryAll: '전체 옵션 적용 {n}개',
  summaryPartial: '일부 옵션 적용 {n}개',
  summaryNone: '미적용',
  exposeHelpButton: '앱 노출 상태 안내',
  // 정책을 쓰지 않는 셀러도 [추가]·[엑셀 일괄]은 눌린다 — 누르면 오늘출발 시작 설정(`START_SETUP`)이 먼저 열린다(PM 2026-10-13)
  policyOffNotice:
    '오늘출발을 사용하고 있지 않아 앱에는 모두 일반배송으로 보입니다. [추가]나 [엑셀 일괄]을 누르면 오늘출발 시작 설정이 먼저 열립니다.',
  holidayNotice: '임시 휴무 기간이라 앱에는 모두 일반배송으로 보입니다.',
  // 국내배송만 보여 준다(PM 2026-09-28) — 요약 카드 아래 안내 한 줄. 배송구분 필터·칸은 없앴다
  // 판매 Y·진열 Y 상품만 보인다(PM 2026-09-30, adminapi `_option_scope`) — 용어는 상품 리스트 필터(판매·진열)와 같게
  // 목록 = 판매 Y·진열 Y 상품의 오늘출발(02) 옵션 줄만(PM 2026-09-30) — 이 화면엔 [오늘출발 지정]이 없으므로 지정 경로를 함께 알린다
  domesticOnlyNotice:
    '판매·진열 중인 상품의 오늘출발 옵션만 보입니다. 새로 지정하려면 [오늘출발 상품 추가하기]나 [엑셀 일괄 지정]을 이용해 주세요.',
  // 위 안내의 두 번째 줄(PM 2026-10-01) — 이 목록에 안 보이는 미판매·미진열 상품의 오늘출발을 끄는 길. 메뉴 이름은 상품 리스트
  // `PRODUCT_LIST.menuButton`·`unsetToday` 와 같은 단어다(문장을 그대로 둔다 — 상수를 이어 붙이지 않는다)
  hiddenProductsNotice:
    '미판매·미진열 상품의 오늘출발을 해제하려면 상품 리스트의 [배송변경] > [오늘출발 해제]나 [엑셀 일괄 지정]을 이용해 주세요.',
  keywordPlaceholder: '상품명을 입력해 주세요.',
  productNoPlaceholder: '상품번호',
  empty: '검색된 옵션이 없습니다.',
  // 조건 없이 비었을 때 = 오늘출발 옵션이 하나도 없다(지정 경로 안내)
  emptyNoToday: '오늘출발로 지정한 옵션이 없습니다. [오늘출발 상품 추가하기]나 [엑셀 일괄 지정]으로 지정해 주세요.',
  setToday: '오늘출발 지정',
  unsetToday: '오늘출발 해제',
  // [오늘출발 해제] 확인 창(PM 2026-10-01 ⑤) — 상품 리스트 [배송변경] 확인 문장(`PRODUCT_LIST.changeConfirmNormal`)의 '상품' 만 '옵션' 으로.
  // `{label}` 은 늘 '일반배송' 이라 조사는 '으로'. 그 자리는 화면이 굵게 그린다(상품 리스트와 같다)
  unsetConfirm: '선택한 옵션 {n}개를 {label}으로 변경합니다.',
  optionColumn: '옵션명',
  applyColumn: '오늘출발 적용',
  deliveryCodeColumn: '오늘출발 설정',
  // 옵션을 오늘출발로 추가하거나 변경한 일시(PM 2026-10-26) — 상품 수정일시가 아니다. 설명은 칸 제목 (?) 도움말로만 보여 준다
  appliedAtColumn: '오늘출발 적용일시',
  appliedAtHelp: '옵션을 오늘출발로 추가하거나 변경한 일시입니다.',
  stockColumn: '재고',
  exposeColumn: '앱 노출 상태',
  stockUnmanaged: '재고관리 안 함',
  changeResultTitle: '배송타입 변경 결과',
  failureColumnNo: '옵션번호',
  failureColumnReason: '변경하지 못한 사유',
  /** 앞에 건수가 붙는다 — "12개 옵션이 변경되었습니다." */
  changeSuccessSuffix: '개 옵션이 변경되었습니다.',
  changeFailed: '배송타입을 변경하지 못했습니다. 다시 시도해 주세요.',
  // 앱 노출 상태 도움말 — 시안 `od-product-reg.jsx` BoardExposureHelp(1150px · 5상태 표 + 앱 미리보기 + 노출 위치 2곳).
  // 진입점은 표 헤더 '앱 노출 상태' 옆 ? 아이콘(PM 2026-09-30 "시안과 다르다"). 앱 화면 글자는 `todayDeliveryAppPreview`.
  exposeHelpTitle: '앱 노출 상태',
  exposeHelpLead: '옵션이 지금 하이버 앱에서 어떻게 보이는지 나타내는 항목입니다.',
  exposeHelpStateHeader: '상태',
  exposeHelpPreviewHeader: '하이버 앱에서 보이는 모습',
  exposeHelpTodayDesc: '오늘출발로 판매 중인 옵션입니다.',
  exposeHelpTodayNote: '옵션 선택 창에는 [오늘출발] 칩과 남은 재고가 보이고, 출발 예정일은 상품 상세의 배송 줄에서 안내합니다.',
  // 이 목록은 오늘출발 옵션 줄만 보여 준다(PM 2026-09-30) — 일반배송 상태는 오늘출발을 쓰지 않는 스토어에서만 나온다
  exposeHelpNormalIntro:
    '오늘출발로 지정했지만 오늘출발을 사용하고 있지 않아 앱에 일반배송으로 보이는 옵션입니다. 배송정책 관리에서 오늘출발을 켜면 오늘출발로 보입니다.',
  exposeHelpNormalConvert:
    "재고 소진 시 판매방식을 '일반배송 전환'으로 설정한 옵션은 재고가 0이 되면 일반배송으로 바뀌고 이 목록에서 빠집니다. 오늘출발 옵션이 하나도 없는 상품은 옵션 선택 창에 배송 구분을 표시하지 않습니다.",
  exposeHelpSoldoutDesc: '오늘출발로 지정했지만 재고가 없어 구매할 수 없는 옵션입니다.',
  exposeHelpSoldoutNote: '옵션 선택 창에서 회색으로 보이고 배송 구분은 표시하지 않습니다.',
  // 휴무·주말은 목록 값(3가지)이 아니라 상황이다 — 표 밖 '상황' 구역에서 목록에 무엇으로 보이는지와 함께 설명한다
  exposeHelpSituationTitle: '이럴 때는 앱에서 이렇게 보입니다',
  exposeHelpListShows: '목록 표시',
  exposeHelpHolidayChip: '임시 휴무 중',
  exposeHelpHolidaySub: '뱃지 숨김',
  exposeHelpHolidayDesc:
    '휴무 기간에는 상품 상세 배송 줄에서 [오늘출발] 뱃지를 숨기고, 휴무가 끝난 뒤의 출발 예정일과 휴무 안내를 보여 줍니다.',
  // 휴무는 목록 값을 바꾸지 않는다(adminapi `exposure_state` — 휴무는 판정에 넣지 않고 TODAY 유지, 계약 c7)
  exposeHelpHolidayNote: '휴무가 끝나면 상품 상세에 [오늘출발] 뱃지가 다시 보입니다.',
  exposeHelpWeekendChip: '주말·공휴일',
  exposeHelpWeekendSub: '뱃지 유지',
  exposeHelpWeekendDesc: '주말·공휴일에는 상품 상세 배송 줄의 [오늘출발] 뱃지를 그대로 두고, 다음 영업일 출발 예정일을 안내합니다.',
  exposeHelpWhereTitle: '노출 위치 2곳',
  exposeHelpWherePdp: '① 상품 상세 › 배송 줄 — 옵션을 고르기 전에는 상품 전체 기준, 고른 뒤에는 그 옵션 기준입니다.',
  exposeHelpWherePdpNote:
    '오늘출발 옵션이 하나라도 있으면 [오늘출발]과 일부 옵션 안내가 함께 보입니다. 품절은 배송 줄에 나오지 않고 옵션 선택 창에만 보입니다.',
  exposeHelpWhereSheet: '② 상품 상세 › 구매하기 › 옵션 선택 창 — 옵션마다 보입니다.',
  exposeHelpClose: '닫기',
  // [오늘출발 상품 추가하기]·목록 줄 수정·선택 줄 일괄 변경 (PM 2026-09-30) — 창 안 문구는 아래 `ADD_PRODUCT`
  addButton: '오늘출발 상품 추가하기',
  // 재고 칸 — 재고관리를 안 하는 옵션은 빈칸에 `stockUnmanaged` 를 흐리게 보여 주고, 숫자를 넣으면 재고관리로 바뀐다(옵션 편집기와 같은 규칙)
  stockInvalid: '재고수량은 0 이상 30,000 이하의 숫자로 입력해 주세요.',
  noOptionStockHint: '옵션 없는 상품의 재고는 상품 수정에서 바꿀 수 있습니다.',
  rowSave: '저장',
  saved: '저장했습니다.',
  // `{n}` = 저장하지 못한 옵션 수. 뒤에 사유가 붙을 수 있다
  partialFailed: '{n}개 옵션은 저장하지 못했습니다.',
  stockSaveFailed: '재고수량을 저장하지 못했습니다. 다시 시도해 주세요.',
  soldoutSaveFailed: '재고 소진 시 판매방식을 저장하지 못했습니다. 다시 시도해 주세요.',
  // 선택한 줄의 '재고 소진 시 판매방식' 일괄 변경 — 버튼 이름이 [판매방식 변경] 이라 메뉴 항목은 `SOLDOUT_ACTION_LABEL` 값만 쓴다
  soldoutMenu: '판매방식 변경',
  soldoutResultTitle: '판매방식 변경 결과',
}

/**
 * 2b-S3 [오늘출발 상품 추가하기] 창 (PM 2026-09-30) — ① 상품 검색 → ② 옵션별 오늘출발·재고수량·재고 소진 시 판매방식 설정.
 * 칸 이름·선택지는 옵션 편집기와 같은 말을 쓴다(`OPTION_EDITOR.soldoutColumn`·`SOLDOUT_ACTION_LABEL`·`DELIVERY_CODE_LABEL`).
 */
export const ADD_PRODUCT = {
  title: '오늘출발 상품 추가하기',
  stepSearch: '상품을 검색해 주세요.',
  stepOptions: '오늘출발로 설정할 옵션을 고르고 저장해 주세요.',
  searchTypePlaceholder: '검색구분',
  keywordPlaceholder: '검색어를 입력해 주세요.',
  searchButton: '검색',
  searchGuide: '판매·진열 중인 상품만 검색됩니다.',
  keywordRequired: '검색어를 입력해 주세요.',
  productNoInvalid: '상품번호는 숫자로 입력해 주세요.',
  // `{max}` = `PRODUCT_SEARCH_MAX_PRODUCT_NOS`(서버 상한과 같은 값)
  productNoTooMany: '상품번호는 한 번에 {max}개까지 검색할 수 있습니다.',
  searchFailed: '상품을 검색하지 못했습니다. 다시 시도해 주세요.',
  empty: '검색된 상품이 없습니다.',
  todayOptionColumn: '오늘출발 옵션',
  selectButton: '선택',
  researchButton: '다시 검색',
  selectedLabel: '선택한 상품',
  optionColumn: '옵션',
  optionNoColumn: '옵션번호',
  todayColumn: '오늘출발',
  allTodayLabel: '전체 옵션',
  stockColumn: '재고수량',
  optionsLoadFailed: '옵션을 불러오지 못했습니다. 다시 시도해 주세요.',
  optionsEmpty: '옵션이 없습니다.',
  deliverySaveFailed: '오늘출발 설정을 저장하지 못했습니다. 다시 시도해 주세요.',
  // 재고 소진으로 일반배송 전환된 옵션(재고관리 꺼짐)을 다시 오늘출발로 켤 때(PM 2026-10-01) — 서버가 재고 없이는 `TD_STOCK_REQUIRED` 로 거절한다.
  // 옵션 있는 상품은 재고 칸 아래, 옵션 없는 상품(재고 읽기 전용)은 표 아래에 보이고 [저장] 을 막는다
  stockRequiredHint: '재고 소진으로 일반배송 전환된 옵션입니다. 재고 수량을 입력해 주세요.',
  stockRequiredNoOption: '재고 소진으로 일반배송 전환된 상품입니다. 상품 수정에서 재고 수량을 입력한 뒤 지정해 주세요.',
  // 저장하지 못한 옵션 표 — 어느 항목이 왜 안 됐는지
  failureStepColumn: '항목',
  save: '저장',
  close: '닫기',
}

/** 앱 노출 상태 3종 라벨 (adminapi `exposure_state`) */
export const EXPOSE_STATE_LABEL = {
  TODAY: '오늘출발',
  SOLDOUT: '품절',
  NORMAL: '일반배송',
}

/** 배송타입(배송코드) 라벨 */
export const DELIVERY_CODE_LABEL = {
  '01': '일반배송',
  '02': '오늘출발',
}

/**
 * 재고가 0이 됐을 때 동작 — 배송정책 기본값 · 옵션 편집기 · 옵션 관리 공통 (이 순서).
 * 용어는 '재고 소진 시 판매방식 — 품절 유지 / 일반배송 전환' 으로 통일한다(PM 2026-09-30·10-01). 예전 말(칸 '재고가 …면', 선택지 '…로 두기'·'…으로 바꾸기')은 쓰지 않는다.
 */
export const SOLDOUT_ACTION_LABEL = {
  // 시안(od-product-reg.jsx 확정 G5)·설계 메모(ddl KEEP_SOLDOUT)와 같은 말 — 앱 노출 상태 '품절' 과도 구분된다(PM 2026-10-01)
  SOLD_OUT: '품절 유지',
  CONVERT_NORMAL: '일반배송 전환',
}

/** 2b-S4 상품 등록/수정 옵션 편집기 */
export const OPTION_EDITOR = {
  entryGuide: '오늘출발은 옵션별로 설정할 수 있습니다. [옵션 편집]에서 배송타입을 선택해 주세요.',
  // 옵션 없는 상품(상품 자체가 SKU)은 [옵션 편집] 이 없다 — 판매정보의 배송타입 칸에서 고른다(PM 2026-10-01 ①). 같은 안내 자리에 이 문장을 쓴다
  noOptionEntryGuide: '오늘출발은 아래 배송타입에서 선택해 주세요.',
  // 옵션 없는 상품의 현재 배송타입을 읽지 못했을 때 — 모르는 값으로 칸을 그리지 않는다(저장하면 서버 값을 덮는다)
  noOptionLoadFailed: '배송타입을 불러오지 못했습니다. 새로고침해 주세요.',
  // 옵션 없는 상품의 배송타입 칸 (?) 도움말·재고 필수 문구 — 이 화면의 재고 칸은 '재고 관리' 스위치라 편집기 문구('재고수량에서 [관리]를 선택')와
  // 단어가 다르다(PM 2026-10-01 Ⓐ). 옵션 편집기 문구(`columnHelp`·`stockRequired`)는 그대로 둔다
  noOptionColumnHelp: '재고 소진으로 일반배송 전환된 상품은 재고 관리를 켜고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
  noOptionStockRequired: '재고 소진으로 일반배송 전환된 상품입니다. 재고 관리를 켜고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
  // 옵션 없는 상품의 '재고 소진 시 판매방식' (?) 도움말 — 편집기 문장(`soldoutColumnHelp`)의 '옵션' 만 '상품' 으로(이 화면엔 옵션이 없다)
  noOptionSoldoutColumnHelp:
    '재고가 0이 되었을 때 상품을 어떻게 판매할지 정합니다.\n' +
    `${SOLDOUT_ACTION_LABEL.SOLD_OUT}: 품절로 표시되고 구매할 수 없습니다.\n` +
    `${SOLDOUT_ACTION_LABEL.CONVERT_NORMAL}: 오늘출발에서 빠지고, 재고관리가 꺼진 채로 계속 판매됩니다. ` +
    '전환된 상품은 재고 수량을 입력해야 다시 오늘출발로 지정할 수 있습니다.',
  // 옵션 없는 상품 저장 — 판매정보(·배송타입)는 저장됐고 뒤 단계 전송만 실패했다(Astra 2026-10-01 MINOR).
  // '저장 실패 · 판매정보' 로 묶지 않고 이 문장만 알린다. 재시도 안내는 '다시 저장해 주세요' 하나(새로고침하면 남은 변경이 사라진다)
  noOptionDeliveryPartial: '판매정보는 저장했습니다. 배송타입을 저장하지 못했습니다. 다시 저장해 주세요.',
  noOptionSoldoutPartial: '판매정보는 저장했습니다. 재고 소진 시 판매방식을 저장하지 못했습니다. 다시 저장해 주세요.',
  noOptionSoldoutPartialAfterDelivery:
    '판매정보와 배송타입은 저장했습니다. 재고 소진 시 판매방식을 저장하지 못했습니다. 다시 저장해 주세요.',
  // 저장 중 서버가 오늘출발 지정을 '정책 미사용'(`TD_POLICY_OFF`)으로 거절했고, 셀러가 시작 설정을 취소했다(PM 2026-10-13).
  // 옵션 없는 상품은 위 '일부 저장' 문장과 같은 자리, 옵션 상품은 저장 결과 알림에 쓴다
  policyOffPartial: '판매정보는 저장했습니다. 오늘출발 배송타입은 오늘출발을 시작한 뒤 다시 저장해 주세요.',
  column: '배송타입',
  // 배송타입 칸 제목의 (?) 도움말 — 행마다 마우스 오버로 띄우던 사유(`stockRequired`)를 칸 규칙 문장으로 옮겼다
  // (PM 2026-10-01 "마우스 오버 UI는 컬럼명 툴팁으로 모두 대체"). `stockRequired` 는 저장 스낵바(편집기 검증)가 그대로 쓴다
  columnHelp: '재고 소진으로 일반배송 전환된 옵션은 재고수량에서 [관리]를 선택하고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
  batchLabel: '배송타입',
  batchPlaceholder: '선택',
  saveFailed: '오늘출발 배송타입을 저장하지 못했습니다.',
  // 신규 옵션이 저장 뒤 목록에서 확인되지 않은 경우 — 일부만 반영하지 않고 전체를 멈춘다
  matchFailed: '새로 만든 옵션의 오늘출발 배송타입을 확인하지 못했습니다.',
  // 앞에 건수가 붙는다 — "3건의 배송타입을 바꾸지 못했습니다."
  rowsRejectedSuffix: '건의 배송타입을 바꾸지 못했습니다.',
  /** 저장 실패 뒤 재시도 안내 */
  retrySuffix: '남은 변경은 그대로 두었으니 다시 저장해 주세요.',
  /** 상품 저장 화면 — 재시도해도 같은 실패(옵션 합 상한 초과)와 재시도할 실패가 섞였을 때, 재시도할 항목만 짚는다. `{labels}` = '상품정보·상세정보' */
  retryLabelsHint: '실패한 항목 중 {labels} 항목은 새로고침 후 다시 저장해 주세요.',
  // 모더레이터 결정(2026-09-15 ②) — 중복 등록 사고를 막는 게 길이보다 중요하다
  createFailed:
    '상품 등록은 끝났습니다. 다시 등록하지 말아 주세요.<br/>오늘출발 배송타입만 반영되지 않았으니 오늘출발 옵션 관리에서 지정해 주세요.',
  soldoutColumn: '재고 소진 시 판매방식',
  // 칸 제목의 (?) 도움말 — 칸 전체(두 선택지)를 설명한다(PM 2026-10-01 "마우스 오버 UI는 컬럼명 툴팁으로 모두 대체").
  // 줄바꿈(\n)은 툴팁이 그대로 줄로 보여 준다. 사실 근거: 위키 `notes/260921-재고소진-동작-설계.md` §3·§5 —
  // 품절 유지 = 배송코드 02 그대로 품절(구매 불가), 일반배송 전환 = 01·재고관리 끔·계속 판매, 다시 지정은 재고 수량 필수(`TD_STOCK_REQUIRED`).
  // '재고를 넣으면 다시 판매' 는 쓰지 않는다 — 주문 소진 경로가 판매 YN 을 N 으로 내리고 옵션 저장은 그 값을 되돌리지 않아 보장되지 않는다.
  // 선택지 행에만 뜨던 `soldoutConvertHelp1·2` 는 이제 화면에서 쓰지 않는다 — 셀러 가이드와 같은 확정문이라 하니스가 고정해 둔다
  soldoutColumnHelp:
    '재고가 0이 되었을 때 옵션을 어떻게 판매할지 정합니다.\n' +
    `${SOLDOUT_ACTION_LABEL.SOLD_OUT}: 품절로 표시되고 구매할 수 없습니다.\n` +
    `${SOLDOUT_ACTION_LABEL.CONVERT_NORMAL}: 오늘출발에서 빠지고, 재고관리가 꺼진 채로 계속 판매됩니다. ` +
    '전환된 옵션은 재고 수량을 입력해야 다시 오늘출발로 지정할 수 있습니다.',
  soldoutBatchLabel: '재고 소진 시 판매방식',
  soldoutConvertHelp1: '재고가 0이 되면 오늘출발에서 빠지고, 재고관리가 꺼진 채로 계속 판매됩니다.',
  soldoutConvertHelp2: '다시 오늘출발로 만들려면 옵션에서 배송타입과 재고를 다시 지정해 주세요.',
  // 재고 소진으로 일반배송 전환된 옵션을 재고관리 없이 다시 오늘출발로 바꾸면 저장을 막는다(PM 2026-10-01, 서버 `TD_STOCK_REQUIRED`).
  // 편집기 재고 칸은 제목 '재고수량' 에 라디오 '관리X / 관리' 라 그 단어로 쓴다(확정본의 '재고관리를 켜고' 를 화면 라벨에 맞춤)
  stockRequired: '재고 소진으로 일반배송 전환된 옵션입니다. 재고수량에서 [관리]를 선택하고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
}

/**
 * 2b-E2 셀러 이행률 — **제목 + 값 + 설명**(PM 2026-09-30 "타이틀이 없어 무슨 내용인지 알 수 없다").
 * 옵션 관리 상단 요약 띠(B안 확정, PM 2026-09-30)의 가운데 칸에 그린다 — 설명은 값 옆 한 줄이라 짧게 쓴다.
 * `{rate}`·`{count}` 는 화면이 채우고, `{days}`·`{min}` 은 상수(`TODAY_DELIVERY_PERFORMANCE_DAYS`·`…_MIN_SAMPLE`)로 채운다.
 * 조회 중(응답 전)에는 제목만 보인다 — 받지도 않은 '0건' 을 미리 찍지 않는다.
 */
export const PERFORMANCE = {
  title: '오늘출발 약속 이행률',
  readyValue: '{rate}%',
  readyDescription: '최근 {days}일 · 표본 {count}건 · 약속한 날에 출발한 주문의 비율',
  collectingValue: '데이터 수집 중',
  collectingDescription: '최근 {days}일 주문 {count}건 / {min}건 · {min}건이 쌓이면 비율이 표시됩니다.',
  failedValue: '불러오지 못했습니다',
  failedDescription: '잠시 뒤 다시 확인해 주세요. 오늘출발 노출에는 영향이 없습니다.',
}

/** 2b-E1 엑셀 일괄 지정 */
export const BULK_EXCEL = {
  openButton: '엑셀 일괄 지정',
  dialogTitle: '엑셀 일괄 지정',
  templateButton: '양식 다운로드',
  listDownloadButton: '목록 다운로드',
  listDownloadFileName: '오늘출발_옵션_목록.xlsx',
  listDownloadFailed: '목록을 내려받지 못했습니다. 다시 시도해 주세요.',
  uploadLabel: '엑셀 업로드',
  resultButton: '결과 파일 다운로드',
  templateFileName: '오늘출발_상품_일괄지정_양식.xlsx',
  // 양식 = `상품번호 | 옵션번호 | 오늘출발(사용/미사용)` + '작성 방법' 시트 (PM 2026-09-28, adminapi 양식과 같이 바뀐다)
  guide1: '양식을 내려받아 상품번호와 오늘출발(사용/미사용)을 채운 뒤 업로드해 주세요. 옵션번호를 비우면 상품의 모든 옵션에 적용됩니다.',
  // 새 양식 2~4행의 회색 예시는 서버·화면이 건너뛴다(행 수에도 세지 않는다) — 지우느라 헤맬 필요가 없다는 안내(PM 2026-09-30)
  guideExample: '양식의 회색 예시 행은 지우지 않아도 됩니다.',
  guide2: '한 번에 5,000행 · 2MB 까지 올릴 수 있습니다.',
  guide3: '내 스토어의 국내배송 상품만 오늘출발로 지정할 수 있고, 처리하지 못한 행은 결과 파일에 사유가 적힙니다.',
  templateFailed: '양식을 내려받지 못했습니다. 다시 시도해 주세요.',
  uploadFailed: '엑셀을 업로드하지 못했습니다. 다시 시도해 주세요.',
  resultFailed: '결과 파일을 내려받지 못했습니다. 다시 시도해 주세요.',
  resultExpired: '결과 파일 보존 기간(7일)이 지났습니다. 다시 업로드해 주세요.',
  // 화면이 잡 번호를 모를 때 — 버튼을 눌러도 아무 반응이 없으면 고장으로 보인다(스테이징 QA SA-040, 2026-10-01)
  resultNotFound: '결과 파일을 찾을 수 없습니다. 다시 업로드해 주세요.',
  pollTimeout: '처리가 지연되고 있습니다. 잠시 후 결과를 다시 확인해 주세요.',
  pollFailed: '진행 상태를 확인하지 못했습니다. 잠시 후 다시 확인해 주세요.',
  // 직전 잡을 되살리지 못했을 때 — 기록은 남겨 두므로 다시 열면 재시도된다
  restoreFailed: '직전 처리 결과를 불러오지 못했습니다. 잠시 후 다시 열어 주세요.',
  parseFailed: '엑셀 양식이 올바르지 않습니다.',
  readFailed: '파일을 읽지 못했습니다.',
  progressDone: '처리 완료',
  progressFailed: '처리에 실패했습니다.',
  progressRunning: '처리 중',
}

/** 입력값 검증 — 화면에서 서버보다 먼저 막는 것들 */
export const VALIDATION = {
  cutoffRequired: '주문 마감시각을 선택해 주세요.',
  fileRequired: '파일을 선택해 주세요.',
  fileNotXlsx: 'xlsx 파일만 업로드할 수 있습니다.',
  // 아래 셋은 엑셀 일괄 지정 업로드를 서버보다 먼저 막을 때의 문구 — adminapi `today_delivery/excel.py` `parse_upload` 의
  // 거절 문구와 **글자 그대로 같다**(PM 2026-10-01 ④ — 화면이 먼저 막든 서버가 막든 같은 말). 서버 문구가 바뀌면 같이 고친다
  // `TD_EXCEL_TOO_LARGE` — `{mb}` 는 `BULK_EXCEL_MAX_BYTES`(서버 `MAX_FILE_BYTES` 와 같은 값)를 MB 로
  fileTooLarge: '엑셀 파일은 {mb}MB 이하만 올릴 수 있습니다.',
  // `TD_EXCEL_EMPTY` — 시트에 헤더 말고 아무 행도 없다
  rowsEmpty: '엑셀에 데이터가 없습니다.',
  // `TD_EXCEL_EMPTY` — 양식의 예시 행(A열 `예시…`)만 남아 있다
  rowsExampleOnly: '예시 행 말고 처리할 행이 없습니다. 상품번호를 적어 주세요.',
  // `TD_EXCEL_TOO_LARGE`(행 상한) — `{max}` 는 `BULK_EXCEL_MAX_ROWS`(서버 `MAX_ROWS`)를 천 단위 콤마로(Ⓑ)
  rowsTooMany: '엑셀은 최대 {max}행까지 올릴 수 있습니다.',
}

/**
 * 재고 소진 시 판매방식 변경에서만 다르게 읽히는 사유 — 나머지 코드는 `FAILURE_REASON` 그대로.
 * `TD_NOT_OWNER` 의 기본 문구는 '지정' 이라 판매방식을 바꾸는 맥락과 맞지 않는다(PM 2026-09-30 문구 검토).
 */
export const SOLDOUT_FAILURE_REASON = {
  // 서버는 없는·삭제된·남의 상품 번호를 모두 이 코드로 거절한다(존재 여부를 드러내지 않음) — 셀러가 실제로 보는 경우는 목록을 연 뒤 삭제된 상품이라 '남의 상품' 처럼 읽히지 않게 쓴다(PM 2026-10-01)
  TD_NOT_OWNER: '상품 정보를 찾을 수 없습니다. 새로고침한 뒤 다시 시도해 주세요.',
}

/**
 * 옵션 없는 상품(상품 수정 판매정보의 배송타입 칸) 저장이 거절됐을 때 — `FAILURE_REASON`·`SOLDOUT_FAILURE_REASON` 중 '옵션' 이라고 말하는 사유만
 * '상품' 으로 바꾼 문장(이 화면엔 옵션이 없다). 나머지 사유는 공통 문장을 그대로 쓴다
 */
export const NO_OPTION_FAILURE_REASON = {
  TD_STOCK_REQUIRED: '재고 소진으로 일반배송 전환된 상품은 재고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
  TD_NOT_TODAY: '오늘출발 상품이 아닙니다.',
  TD_NO_DETAIL_INFO: '상품 정보를 찾을 수 없어 판매방식을 바꾸지 못했습니다.',
}

/** 배송코드 변경 실패 사유 코드 → 셀러 안내 (서버는 코드만 준다) */
export const FAILURE_REASON = {
  // adminapi `writer._reject_reason` 이 이 코드로 거절하는 경우 = 내 상품이 아님 · 삭제됨(사용여부 Y/T 아님) ·
  // 하이버 상품이 아님 · 대표상품이 하이버가 아님. **판매상태(판매중지 N/M)는 보지 않는다** — 판매중지 상품도 지정할 수 있다
  // (예전 주석의 '판매중지도 이 코드로 묶는다' 는 사실이 아니었다, 2026-09-28 정정)
  TD_NOT_OWNER: '내 상품만 지정할 수 있습니다. (삭제된 상품은 제외)',
  TD_NOT_DOMESTIC: '국내배송 상품만 오늘출발로 지정할 수 있습니다.',
  TD_SELLER_SUSPENDED: '운영자가 오늘출발을 중지한 상태입니다.',
  TD_INVALID_ROW: '입력값이 올바르지 않습니다.',
  // 재고 소진 시 판매방식 변경(`PUT …/options/soldout-action`) — 오늘출발 옵션에만 있는 설정이다
  TD_NOT_TODAY: '오늘출발 옵션이 아닙니다.',
  // 옵션의 상세 정보 행이 없어 '일반배송 전환' 를 저장할 곳이 없다(서버가 행을 새로 만들지 않는다)
  TD_NO_DETAIL_INFO: '옵션 정보를 찾을 수 없어 판매방식을 바꾸지 못했습니다.',
  // 대상 SKU 합 상한 초과(adminapi 6ad42b2) — `{max}` 는 화면이 천 단위 콤마로 채운다(`utils/todayDelivery.tooManyOptionsMessage`).
  // 서버 메시지는 기본값("Bad Request")이라 이 코드는 서버 문구 대신 이 문구를 쓴다 — 배송변경·옵션 지정/해제·옵션 편집기 저장·엑셀 결과 사유 공통
  TD_TOO_MANY_OPTIONS: '선택한 상품의 옵션이 {max}개를 넘어 한 번에 변경할 수 없습니다. 상품을 나눠서 변경해 주세요.',
  // 재고 소진으로 일반배송 전환된 옵션(전환 표시 Y)을 재고관리 없이 오늘출발로 지정(PM 2026-10-01) — 상품 단위 지정은 소속 옵션 중
  // 하나라도 걸리면 상품 행 전체가 이 코드다. adminapi `FAILURE_REASON_MESSAGES` 와 **같은 문구**(엑셀 결과 파일에도 같은 말이 나간다)
  TD_STOCK_REQUIRED: '재고 소진으로 일반배송 전환된 옵션은 재고 수량을 입력해야 오늘출발로 지정할 수 있습니다.',
  // 지정(01→02)은 정책 활성 셀러만(PM 2026-10-13) — adminapi `FAILURE_REASON_MESSAGES` 와 같은 문구(엑셀 접수 409 도 같은 말).
  // 정책 OFF·정책 행 없음이면 `TD_POLICY_OFF`, ON 인데 참여 제한이면 `TD_PARTICIPATION_LOCKED`
  TD_POLICY_OFF: '오늘출발을 시작한 뒤 지정할 수 있습니다.',
  TD_PARTICIPATION_LOCKED: SERVER_MIRROR.participationLocked,
  DEFAULT: '처리하지 못했습니다.',
}
